import * as React from "react";
import { NavLink } from "react-router-dom";
import { navGroups } from "@/lib/navigation";
import { cn } from "@/lib/utils";
import { useAuth } from "@/supabase/AuthContext";

type Props = {
  onNavigate?: () => void;
};

export const Sidebar: React.FC<Props> = ({ onNavigate }) => {
  const { status, session, memberships, membershipError, missing } = useAuth();

  const connectionLabel =
    status === "not_configured"
      ? `Supabase yapılandırılmadı${missing.length ? `: ${missing.join(", ")}` : ""}`
      : status === "loading"
        ? "Oturum kontrol ediliyor…"
        : !session
          ? "Oturum açılmadı"
          : membershipError
            ? "Şirket üyeliği okunamadı"
            : memberships === null
              ? "Şirket üyeliği yükleniyor…"
              : memberships.length === 0
                ? "Şirket üyeliği bulunamadı"
                : "Bağlantı hazır";

  const connectionTone =
    status === "not_configured" || membershipError
      ? "bg-destructive"
      : status === "ready" && session && memberships && memberships.length > 0
        ? "bg-emerald-500"
        : "bg-amber-500";

  return (
    <aside
      data-testid="app-sidebar"
      className="relative flex h-full w-full flex-col overflow-hidden border-r border-slate-800 bg-[#111827] text-white"
    >
      <div className="flex shrink-0 flex-col items-center justify-center gap-1.5 border-b border-slate-800 px-3 py-4 text-center">
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/d/da/BesiktasJK-Logo.svg"
          alt="Beşiktaş Jimnastik Kulübü arması"
          width="34"
          height="56"
          loading="eager"
          decoding="async"
          className="h-14 w-[34px] object-contain"
        />
        <div className="leading-tight">
          <div className="font-serif text-[16px] font-semibold text-white">Kartalyuvası</div>
          <div className="mt-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
            IFM Analiz
          </div>
        </div>
      </div>

      <nav className="relative z-10 min-h-0 flex-1 space-y-3 overflow-y-auto bg-[#111827] px-2.5 py-3" aria-label="Ana gezinme">
        {navGroups.map((group) => (
          <section key={group.label} aria-label={group.label}>
            <div className="px-2.5 pb-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              {group.label}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    data-testid={item.testId}
                    onClick={onNavigate}
                    title={item.label}
                    className={({ isActive }) =>
                      cn(
                        "group flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[12px] transition-colors",
                        isActive
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={cn(
                            "h-4 w-4 shrink-0",
                            isActive
                              ? "text-slate-900"
                              : "text-slate-400 group-hover:text-white",
                          )}
                          strokeWidth={1.75}
                        />
                        <span className="min-w-0 truncate font-medium">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>

      <div
        aria-hidden="true"
        className="relative h-[170px] shrink-0 overflow-hidden border-t border-slate-800 bg-[#080d18]"
      >
        <img
          src="/sidebar-eagle.webp"
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05080f]/35 via-transparent to-[#111827]/10" />
      </div>

      <div className="relative z-10 shrink-0 border-t border-slate-800 bg-[#111827] px-4 py-2">
        <div
          className="flex items-center gap-2 text-[10px] text-slate-400"
          role="status"
          aria-live="polite"
          data-testid="connection-status"
          title={membershipError ?? undefined}
        >
          <span className={cn("inline-flex h-2 w-2 shrink-0 rounded-full", connectionTone)} />
          <span>{connectionLabel}</span>
        </div>
      </div>
    </aside>
  );
};
